import { x } from '@xstyled/styled-components';
import React from 'react';
import { MediaPlayerProvider } from '~/ui/video-player/MediaPlayerProvider';
import { AUDIO_POPUP } from '..';
import { Item, ItemDefinition, registry, RenderProps } from '../Item';
import { PopupButton } from './components/PopupButton';

interface Props {
  positionAtZeroCoordinates?: boolean;
  /** Learner control envelope used by the version-2 editor placement guard. */
  placementControlSize?: number;
  mediaUrl?: string;
  /** Real backend audio id, used by the editor to preserve audio on edit. */
  audioId?: number;
  subtitlesUrl?: string;
  autoPlay?: boolean;
  name?: string;
  body?: string;
  empty?: boolean;
  top?: number;
  left?: number;
  pixelCoordinates?: string;
  shouldShow?: boolean;
  title?: number | string;

  /**
   * When true, the popup will prevent the next button from showing until the popup's
   * audio has been played. If there is no audio, opening the popup will unblock.
   */
  blocking?: boolean;
  replay?: boolean;
  isPopupCentered?: boolean;
  /** When true, place the portal card under the hotspot. Ignored if `isPopupCentered` is true. */
  openPopupUnderButton?: boolean;
  popupFixedOffsetLeftPx?: number;
  popupFixedOffsetTopPx?: number;
  btnSize?: number;
  scroll?: boolean;
  parentId?: string;
  editable?: boolean;
  generated?: boolean;
}

export type AudioPopupDefinition = ItemDefinition<typeof AUDIO_POPUP, Props>;

export class AudioPopup extends Item<typeof AUDIO_POPUP, Props> {
  protected render({
    positionAtZeroCoordinates = false,
    mediaUrl,
    id,
    name,
    top,
    empty,
    left,
    shouldShow = true,
    blocking = false,
    subtitlesUrl,
    body,
    title,
    replay = false,
    isPopupCentered = false,
    openPopupUnderButton = false,
    popupFixedOffsetLeftPx,
    popupFixedOffsetTopPx,
    btnSize,
    scroll = false,
    parentId,
    editable = true,
    generated = false
  }: RenderProps<Props>) {
    const popupContent = (
      <MediaPlayerProvider>
        <PopupButton
          mediaUrl={mediaUrl}
          subtitlesUrl={subtitlesUrl}
          id={id}
          shouldShow={shouldShow}
          name={name || ''}
          body={body || ''}
          blocking={blocking}
          replay={replay}
          title={title}
          isPopupCentered={isPopupCentered}
          openPopupUnderButton={openPopupUnderButton}
          popupFixedOffsetLeftPx={popupFixedOffsetLeftPx}
          popupFixedOffsetTopPx={popupFixedOffsetTopPx}
          btnSize={btnSize}
          scroll={scroll}
          parentId={parentId}
          editable={editable}
          generated={generated}
        />
      </MediaPlayerProvider>
    );

    const positioned = positionAtZeroCoordinates
      ? top != null && left != null
      : top && left;
    return positioned ? (
      <x.div
        position="absolute"
        top={`${top}%`}
        left={`${left}%`}
        style={{ transform: 'translate(-50%, -50%)' }}
      >
        {popupContent}
      </x.div>
    ) : (
      popupContent
    );
  }
}

registry[AUDIO_POPUP] = AudioPopup;
